import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import BackButton from '@luna-protocol/core/src/components/BackButton/BackButton';
import Banner from '@luna-protocol/core/src/components/Banner/Banner';
import Body from '@luna-protocol/core/src/components/Body/Body';
import Card from '@luna-protocol/core/src/components/Card/Card';
import CustomerName from '@luna-protocol/core/src/components/CustomerName/CustomerName';
import { useAuth } from '@luna-protocol/core/src/utils/useAuth.ts';

import { useAppStore } from '../../useAppStore.ts';
import { useGetSerialNumbers } from '../../queries/useGetSerialNumbers.ts';
import { usePutSerialNumbers } from '../../queries/usePutSerialNumbers.ts';
import { buildSerialNumberProducts } from './getProducts.tsx';
import ProductSummary from '../ProductSummary/ProductSummary.tsx';
import messages from './AmendDetails.messages.ts';
import { ProductItem } from '../../types.ts';
import './AmendDetails.scss';
import { RETRIEVE_SERIAL_NUMBERS } from '../../queries/constants.ts';
import { getSerialNumbers } from '../../queries/useGetSerialNumbers.ts';

const AmendDetails = () => {
  useAuth();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { putUpdatedProductSummary } = usePutSerialNumbers();
  const loanApplicationID = useAppStore(state => state.loanApplicationID);
  const customerName = useAppStore(state => state.customerName);
  const setCustomerName = useAppStore(state => state.setCustomerName);
  const serialNumbers = useAppStore(state => state.serialNumbers);
  const setSerialNumbers = useAppStore(state => state.setSerialNumbers);
  const [refetchPassthrough, setRefetchPassthrough] = useState(false);
  const { data, refetch } = useGetSerialNumbers(loanApplicationID);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (data === undefined) return;
    if (loanApplicationID) {
      refetch();
      setSerialNumbers(data);
      setCustomerName(data.primary_customer_name);
      setRefetchPassthrough(true);
    }
  }, [data, loanApplicationID]);

  useEffect(() => {
    if (data === undefined) return;
    if (customerName === undefined || customerName === '') {
      setCustomerName(data?.primary_customer_name);
    }
    if (serialNumbers === undefined) {
      setSerialNumbers(data);
    }
  }, [data, customerName, serialNumbers, setCustomerName, setSerialNumbers]);

  const onSubmit = (newProductList: ProductItem[][]) => {
    const serialNumberProducts = newProductList.map(serials => buildSerialNumberProducts(serials));

    const nonSerialNumberItems = (newProductList[0] ?? []).filter(
      item => !item.title.includes('Number') && !item.title.includes('Price'),
    );

    const flatSerialNumbers = serialNumberProducts.flat();

    const getValue = (items: ProductItem[], key: string) => items.find(item => item.title === key)?.value ?? '';

    const requestBody = {
      loan_application_id: loanApplicationID || '',
      item_name: getValue(nonSerialNumberItems, 'Item').toString(),
      serial_numbers: flatSerialNumbers,
    };

    putUpdatedProductSummary(requestBody, {
      onSuccess: async () => {
        queryClient.invalidateQueries(RETRIEVE_SERIAL_NUMBERS);

        if (loanApplicationID) {
          const serialNumbersData = await getSerialNumbers(loanApplicationID);
          setSerialNumbers(serialNumbersData);
        }
        navigate(`/add_products`);
      },
      onError: context => {
        console.error('error', context);
      },
    });
  };

  return (
    <>
      <BackButton path="/prequalification" inverted />
      <Banner alternate>{formatMessage(messages.title)}</Banner>
      <CustomerName applicantName={customerName || ''} />
      <Body fullWidth>
        <div className="amend-details--container">
          <Card variant={'grey'}>
            <ProductSummary
              onSubmit={onSubmit}
              refetchTrigger={refetchPassthrough}
              setRefetchTrigger={setRefetchPassthrough}
              approvedAmount={data?.approved_amount}
            />
          </Card>
        </div>
      </Body>
    </>
  );
};

export default AmendDetails;
